import type {RegisterStep, RegisterUrlData, UserName} from './types';

import {Button, Input} from '@ds/components';
import {FullScreenLayout, PageLayout} from '@ds/layout';
import {toast} from '@Toast';
import Logo from 'assets/icons/login/logo.svg';
import {useURLFilter} from 'hooks/useURLFilter';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {useNavigate, useSearchParams} from 'react-router-dom';
import {load} from 'recaptcha-v3';
import {useAppDispatch, useAppSelector} from 'store';
import {setCaptchaToken} from 'store/slices/login/slicer';
import {getCaptcha} from 'store/slices/login/thunk';
import {
  loginMobieWithToken,
  loginWithNoPass,
  registerWithOptionalPass
} from 'store/slices/vendor-registration/thunk';
import {mobileValidation, p2e, sanitizeRedirect} from 'utils/Helper';

import OtpVerification from './components/OtpVerification/OtpVerification';
import UserForm from './components/UserForm/UserForm';

const REGISTER_STEP_TITLES: Record<RegisterStep, string> = {
  login: 'ورود به حساب کاربری',
  register: 'ایجاد حساب کاربری',
  otp: 'تایید شماره همراه'
};

const UserRegister: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTo = sanitizeRedirect(
    searchParams.get('redirect') ?? '',
    '/vendor-register-overview'
  );
  const defaultUrlData = useMemo<RegisterUrlData>(() => ({}), []);
  const [urlData, setUrlData] = useURLFilter<RegisterUrlData>('data', defaultUrlData);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [otp, setOtp] = useState('');
  const [countDown, setCountDown] = useState(0);
  const [user, setUser] = useState<UserName>({name: '', lastname: ''});
  const {captcha, captchaToken} = useAppSelector((state) => state.login);
  const step = urlData.step ?? 'login';

  const handleUserForm = (field: keyof UserName, event: React.ChangeEvent<HTMLInputElement>) => {
    setUser((currentUser) => ({...currentUser, [field]: event.target.value}));
  };

  const handlePhoneNumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPhoneNumber(p2e(event.target.value));
  };

  const handleLogin = () => {
    const normalizedPhoneNumber = p2e(phoneNumber);

    if (!mobileValidation(normalizedPhoneNumber)) {
      toast('فرمت شماره موبایل صحیح نمیباشد.');
      return;
    }

    const formData = new FormData();
    formData.append('cellphone', normalizedPhoneNumber);
    formData.append('optionalLoginToken', 'true');
    if (captcha?.is_captcha_enabled && captchaToken) {
      formData.append('captcha', captchaToken);
    }

    void dispatch(loginWithNoPass(formData))
      .unwrap()
      .then(() => {
        setOtp('');
        setUrlData({phoneNumber: normalizedPhoneNumber, step: 'otp'});
      })
      .catch((error) => {
        toast(error.message || 'خطا');
      });
  };

  const verifyOtp = () => {
    if (otp.length !== 6 || !urlData.phoneNumber) {
      return;
    }

    void dispatch(
      loginMobieWithToken({
        cellphone: urlData.phoneNumber,
        code: otp,
        captcha: captcha?.is_captcha_enabled ? captchaToken : undefined
      })
    )
      .unwrap()
      .then((response) => {
        if (response?.data?.is_registered) {
          localStorage.setItem('access_token', response.data.oauth2_token.access_token);
          localStorage.setItem('refresh_token', response.data.oauth2_token.refresh_token);
          localStorage.setItem('isVendor', 'false');
          navigate(redirectTo);
          return;
        }

        setUrlData({
          step: 'register',
          cellphone: urlData.phoneNumber,
          code: otp
        });
      })
      .catch((error) => {
        toast(error.message || 'خطا');
      });
  };

  const registerUser = () => {
    if (!urlData.code || !urlData.cellphone) {
      return;
    }

    void dispatch(
      registerWithOptionalPass({
        firstname: user.name,
        lastname: user.lastname,
        code: urlData.code,
        cellphone: urlData.cellphone
      })
    )
      .unwrap()
      .then((response) => {
        localStorage.setItem('access_token', response.data.oauth2_token.access_token);
        localStorage.setItem('refresh_token', response.data.oauth2_token.refresh_token);
        localStorage.setItem('isVendor', 'false');
        navigate(redirectTo);
      })
      .catch((error) => {
        toast(error.message || 'خطا');
      });
  };

  const handleContinue = () => {
    if (step === 'login') {
      handleLogin();
    } else if (step === 'otp') {
      verifyOtp();
    } else {
      registerUser();
    }
  };

  const handleRecaptcha = useCallback(async () => {
    const recaptcha = await load(import.meta.env.VITE_PUBLIC_RECAPTCHA_SITE_KEY, {
      useRecaptchaNet: true,
      autoHideBadge: true
    });
    const token = await recaptcha.execute('submit');
    dispatch(setCaptchaToken(token));
  }, [dispatch]);

  useEffect(() => {
    void dispatch(getCaptcha())
      .unwrap()
      .then((response) => {
        if (response?.data?.is_captcha_enabled) {
          void handleRecaptcha();
        }

        if (localStorage.getItem('access_token') && localStorage.getItem('refresh_token')) {
          navigate(redirectTo);
        }
      });
  }, [dispatch, handleRecaptcha, navigate, redirectTo]);

  useEffect(() => {
    if (!urlData.step) {
      setUrlData({...urlData, step: 'login'});
    }
  }, [setUrlData, urlData]);

  const isButtonDisabled =
    (step === 'login' && phoneNumber.length !== 11) ||
    (step === 'otp' && otp.length !== 6) ||
    (step === 'register' && (user.name.length < 2 || user.lastname.length < 2));

  return (
    <FullScreenLayout>
      <PageLayout
        className="min-h-0 flex-1"
        footerPosition="static"
        header={
          <header className="flex flex-col items-center gap-4 border-b border-gray-03 px-4 pb-4 pt-6">
            <div className="flex h-20 w-42.5 items-center justify-center">
              <img className="max-h-full max-w-full" src={Logo} alt="نما" />
            </div>
            <h1 className="text-body-md-emphasis text-neutral-fg">{REGISTER_STEP_TITLES[step]}</h1>
          </header>
        }
        footer={
          <Button disabled={isButtonDisabled} fullWidth onClick={handleContinue}>
            ادامه
          </Button>
        }
        contentClassName="px-4 py-6"
      >
        {step === 'login' && (
          <Input
            label="شماره همراه"
            value={phoneNumber}
            placeholder="شماره همراه خود را وارد کنید"
            type="tel"
            inputMode="numeric"
            maxLength={11}
            onChange={handlePhoneNumberChange}
          />
        )}
        {step === 'otp' && (
          <OtpVerification
            otp={otp}
            setOtp={setOtp}
            phoneNumber={urlData.phoneNumber ?? ''}
            countDown={countDown}
            setCountDown={setCountDown}
          />
        )}
        {step === 'register' && <UserForm user={user} onChange={handleUserForm} />}
      </PageLayout>
    </FullScreenLayout>
  );
};

export default UserRegister;
